import {
    Autocomplete, Box, Grid, InputAdornment, Switch, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, Tabs, Tab,
    TextField, Tooltip, Typography,
    FormControlLabel,
    useTheme,
    useMediaQuery,
    Select,
    MenuItem
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { useNavigate } from 'react-router-dom'
import { ApiGetAllUsersTransactions } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import { usePersistedState } from '../../hooks/userPersistedState'

const fmt = (n: number) =>
    (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
const fmtTime = (d: string) =>
    new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const getUserInfo = (u: any) => {
    if (u && typeof u === 'object') return { name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—', email: u.email || '—' }
    return { name: '—', email: '—' }
}

const statusMap: Record<string, { color: string; bg: string; border: string }> = {
    COMPLETED: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    PENDING: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    PROCESSING: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    FAILED: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    CANCELLED: { color: '#9ca3af', bg: '#9ca3af18', border: '#9ca3af50' },
    BUY: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    SELL: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    CASHOUT: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    DIRECT: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    LOAN: { color: '#a78bfa', bg: '#a78bfa18', border: '#a78bfa50' },
    WALLET: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    LINK_BANK: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    SHARE_SELL_DOWN: { color: '#a78bfa', bg: '#a78bfa18', border: '#a78bfa50' },
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
    const s = statusMap[status] || { color: '#9a9a9a', bg: '#9a9a9a18', border: '#9a9a9a50' }
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: '11px', fontWeight: 700,
            color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            letterSpacing: '0.03em', whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            {status}
        </Box>
    )
}

const tabConfig = [
    { label: 'Deposits', icon: <ArrowDownwardIcon sx={{ fontSize: 15 }} /> },
    { label: 'Withdrawals', icon: <ArrowUpwardIcon sx={{ fontSize: 15 }} /> },
    { label: 'Trades', icon: <SwapHorizIcon sx={{ fontSize: 15 }} /> },
    { label: 'Repayments', icon: <AccountBalanceIcon sx={{ fontSize: 15 }} /> },
]

const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333', borderRadius: '14px', p: 2.5,
        position: 'relative', overflow: 'hidden', transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""', position: 'absolute', top: 0, left: 0, right: 0,
            height: '2px', background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const thStyle = {
    backgroundColor: '#241815', color: '#ff6a3d', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const, py: 1.5, borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const, top: 0, zIndex: 2,
}
const tdStyle = { color: '#c0c0c0', py: 1.4, fontSize: 13, whiteSpace: 'nowrap' as const }
const trHover = { transition: 'background 0.15s', '&:hover': { background: '#ff6a3d08' } }
const DEFAULT_PAGE_LIMIT = 10

type TxType = 'DEPOSIT' | 'WITHDRAWAL' | 'TRADE' | 'REPAYMENT'
const tabToType: TxType[] = ['DEPOSIT', 'WITHDRAWAL', 'TRADE', 'REPAYMENT']

const SIDE_OPTIONS = ['BUY', 'SELL']
const ORDER_TYPE_OPTIONS = ['DIRECT', 'LOAN', 'CASHOUT']
const REPAYMENT_TYPE_OPTIONS = ['WALLET', 'LINK_BANK', 'ADJUSTMENT']
const PROVIDER_OPTIONS = ['WIRE', 'AEROPAY', 'REFERRAL']

const searchFieldStyle = {
    width: 180, background: 'transparent', borderRadius: '7px',
    border: '1px solid #898989', transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}

const Transactions: React.FC = () => {
    const navigate = useNavigate()

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('xl'))

    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('transactions', 'all')

    const [tab, setTab] = usePersistedState('transactions:tab', 0)

    const type = tabToType[tab]

    const [stats, setStats] = useState<any>(null)

    const [transactions, setTransactions] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = usePersistedState('transactions:page', 0)
    const [dataPage, setDataPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('transactions:rowsPerPage', DEFAULT_PAGE_LIMIT)
    const [search, setSearch] = usePersistedState('transactions:search', '')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [errorPopup, setErrorPopup] = useState({ open: false, message: '' })
    const [paymentProvider, setPaymentProvider] = usePersistedState('transactions:paymentProvider', '')
    const [side, setSide] = usePersistedState('transactions:side', '')
    const [orderType, setOrderType] = usePersistedState('transactions:orderType', '')
    const [repaymentType, setRepaymentType] = usePersistedState('transactions:repaymentType', '')
    const [hasFees, setHasFees] = usePersistedState('transactions:hasFees', false)
    const requestIdRef = React.useRef(0)

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500)
        return () => clearTimeout(timer)
    }, [search])

    const prevFiltersRef = React.useRef({ debouncedSearch, type, range, customStart, customEnd, paymentProvider, side, orderType, repaymentType, hasFees })
    useEffect(() => {
        const prev = prevFiltersRef.current
        const changed =
            prev.debouncedSearch !== debouncedSearch ||
            prev.type !== type ||
            prev.range !== range ||
            (prev.customStart?.valueOf() !== customStart?.valueOf()) ||
            (prev.customEnd?.valueOf() !== customEnd?.valueOf()) ||
            prev.paymentProvider !== paymentProvider ||
            prev.side !== side ||
            prev.orderType !== orderType ||
            prev.repaymentType !== repaymentType ||
            prev.hasFees !== hasFees
        if (changed) {
            setPage(0)
            setDataPage(0)
            setTransactions([])
        }
        if (prev.type !== type) {
            setPaymentProvider('')
            setSide('')
            setOrderType('')
            setRepaymentType('')
            setHasFees(false)
        }
        prevFiltersRef.current = { debouncedSearch, type, range, customStart, customEnd, paymentProvider, side, orderType, repaymentType, hasFees }
    }, [debouncedSearch, type, range, customStart, customEnd, paymentProvider, side, orderType, repaymentType, hasFees, setPage, setPaymentProvider, setSide, setOrderType, setRepaymentType, setHasFees])
    const fetchData = useCallback(async () => {
        const requestId = ++requestIdRef.current
        try {
            const dateQuery = buildQuery()
            if (dateQuery === null) return
            const filterQuery =
                type === 'DEPOSIT' || type === 'WITHDRAWAL'
                    ? (paymentProvider ? `&paymentProvider=${encodeURIComponent(paymentProvider)}` : '')
                    : type === 'TRADE'
                        ? `${side ? `&side=${encodeURIComponent(side)}` : ''}${orderType ? `&orderType=${encodeURIComponent(orderType)}` : ''}`
                        : type === 'REPAYMENT'
                            ? (repaymentType ? `&repaymentType=${encodeURIComponent(repaymentType)}` : '')
                            : ''
            const res = await ApiGetAllUsersTransactions(
                `?type=${type}&search=${encodeURIComponent(debouncedSearch)}&page=${page + 1}&limit=${rowsPerPage}${dateQuery ? `&${dateQuery}` : ''}${filterQuery}${hasFees ? '&hasFees=true' : ''}`
            )
            if (requestId !== requestIdRef.current) return
            if (res?.status) {
                const d = res.data
                setTransactions(d.transactions || [])
                setTotal(d.pagination?.total || 0)
                setDataPage(page)
                setStats(d.stats)
            }
        } catch (e: any) {
            if (requestId !== requestIdRef.current) return
            setErrorPopup({
                open: true,
                message: e?.message || e?.response?.data?.message || 'Failed to fetch transactions',
            })
        }
    }, [type, debouncedSearch, page, rowsPerPage, buildQuery, paymentProvider, side, orderType, repaymentType, hasFees])

    useEffect(() => { fetchData() }, [fetchData])

    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <Box>
                        <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>
                            Transactions
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />

            <Grid container spacing={2} mb={3} mt={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total Deposited" value={`$${fmt(stats?.deposits?.completedAmount)}`}
                        sub={`${stats?.deposits?.completedCount ?? 0} completed · ${stats?.deposits?.pendingCount ?? 0} pending`}
                        color="#10b981" icon={<ArrowDownwardIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total Withdrawn" value={`$${fmt(stats?.withdrawals?.totalAmount)}`}
                        sub={`${stats?.withdrawals?.count ?? 0} completed`}
                        color="#ef4444" icon={<ArrowUpwardIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Net Balance" value={`$${fmt(stats?.netBalance)}`}
                        sub="Deposits minus withdrawals" color="#3b82f6" icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Trade Volume" value={`$${fmt(stats?.trades?.totalTradeVolume)}`}
                        sub={`${stats?.trades?.totalCount ?? 0} completed trades`}
                        color="#f2674a" icon={<SwapHorizIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total Buys" value={`$${fmt(stats?.trades?.totalBuyAmount)}`}
                        sub={`${stats?.trades?.buyCount ?? 0} buy orders`}
                        color="#10b981" icon={<ArrowDownwardIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total Sells" value={`$${fmt(stats?.trades?.totalSellAmount)}`}
                        sub={`${stats?.trades?.sellCount ?? 0} sell orders`}
                        color="#ef4444" icon={<ArrowUpwardIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total Fees Paid" value={`$${fmt(stats?.trades?.totalFees)}`}
                        sub="Platform + provider fees" color="#f59e0b" icon={<AttachMoneyIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total Repayments" value={`$${fmt(stats?.repayments?.completedAmount)}`}
                        sub={`${stats?.repayments?.completedCount ?? 0} completed · ${stats?.repayments?.pendingCount ?? 0} pending`}
                        color="#a78bfa" icon={<AccountBalanceIcon fontSize="small" />} />
                </Grid>
            </Grid>

            <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    {isMobile ? (
                        <Select
                            value={tab}
                            onChange={(e) => setTab(Number(e.target.value))}
                            size="small"
                            sx={{
                                minHeight: 40, background: '#1e1e1e', borderRadius: '10px',
                                border: '1px solid #333', color: '#fff', fontSize: 13, fontWeight: 600,
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                '& .MuiSvgIcon-root': { color: '#ff6a3d' },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: '#1a1a1a', border: '1px solid #333',
                                        '& .MuiMenuItem-root': {
                                            fontSize: 13, color: '#c0c0c0', gap: 1,
                                            '&:hover': { bgcolor: '#241815' },
                                            '&.Mui-selected': {
                                                color: '#fff', background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                                                '&:hover': { background: 'linear-gradient(135deg, #f2674a, #d94f35)' },
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            {tabConfig.map((t, i) => (
                                <MenuItem key={t.label} value={i} sx={{ display: 'flex', alignItems: 'center' }}>
                                    {t.icon} {t.label}
                                </MenuItem>
                            ))}
                        </Select>
                    ) : (
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            sx={{
                                minHeight: 40, background: '#1e1e1e', borderRadius: '10px', width: 'fit-content',
                                p: 0.5, border: '1px solid #333',
                                '& .MuiTabs-indicator': { display: 'none' },
                                '& .MuiTab-root': {
                                    minHeight: 36, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                                    color: '#6f6f6f', textTransform: 'none', px: 3, py: 0.5, transition: 'all 0.2s',
                                },
                                '& .Mui-selected': {
                                    color: '#fff !important',
                                    background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                                    boxShadow: '0 2px 8px rgba(242,103,74,0.4)',
                                },
                            }}
                        >
                            {tabConfig.map((t) => (
                                <Tab key={t.label} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{t.icon} {t.label}</Box>} />
                            ))}
                        </Tabs>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={hasFees}
                                    onChange={(e) => setHasFees(e.target.checked)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff6a3d' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff6a3d80' },
                                        '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                                    }}
                                />
                            }
                            label={<Typography fontSize={13} color="#c0c0c0">Has Fees</Typography>}
                            sx={{ ml: 0 }}
                        />
                        <TextField
                            placeholder="Search by name or email…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            sx={{
                                width: 280, background: 'transparent', borderRadius: '7px',
                                border: '1px solid #898989', transition: 'border-color 0.3s',
                                '&:hover': { border: '1px solid #ff6a3d' },
                                '&:focus-within': { border: '1px solid #ff6a3d' },
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                '& input': { color: '#fff', fontSize: 13 },
                                '& input::placeholder': { color: '#555', opacity: 1 },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className='color-primary' />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {(type === 'DEPOSIT' || type === 'WITHDRAWAL') && (
                            <Autocomplete
                                size="small"
                                value={paymentProvider}
                                disableClearable={!paymentProvider}
                                onChange={(_, newValue) => setPaymentProvider(newValue || '')}
                                options={PROVIDER_OPTIONS}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Provider" sx={searchFieldStyle}
                                        InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                    />
                                )}
                                PaperComponent={({ children }) => (
                                    <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                        {children}
                                    </Box>
                                )}
                            />
                        )}
                        {type === 'TRADE' && (
                            <>
                                <Autocomplete
                                    size="small"
                                    value={side}
                                    disableClearable={!side}
                                    onChange={(_, newValue) => setSide(newValue || '')}
                                    options={SIDE_OPTIONS}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Side" sx={searchFieldStyle}
                                            InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                        />
                                    )}
                                    PaperComponent={({ children }) => (
                                        <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                            {children}
                                        </Box>
                                    )}
                                />
                                <Autocomplete
                                    size="small"
                                    value={orderType}
                                    disableClearable={!orderType}
                                    onChange={(_, newValue) => setOrderType(newValue || '')}
                                    options={ORDER_TYPE_OPTIONS}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Type" sx={searchFieldStyle}
                                            InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                        />
                                    )}
                                    PaperComponent={({ children }) => (
                                        <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                            {children}
                                        </Box>
                                    )}
                                />
                            </>
                        )}

                        {type === 'REPAYMENT' && (
                            <Autocomplete
                                size="small"
                                value={repaymentType}
                                disableClearable={!repaymentType}
                                onChange={(_, newValue) => setRepaymentType(newValue || '')}
                                options={REPAYMENT_TYPE_OPTIONS}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Repayment Type" sx={searchFieldStyle}
                                        InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                    />
                                )}
                                PaperComponent={({ children }) => (
                                    <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                        {children}
                                    </Box>
                                )}
                            />
                        )}
                    </Box>
                </Box>

                {(type === 'DEPOSIT' || type === 'WITHDRAWAL') && (
                    <TableContainer sx={{ maxHeight: '55vh' }}>
                        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                            <TableHead>
                                <TableRow>
                                    {['#', 'User', 'Transaction ID', 'Date & Time', 'Amount', 'Net Amount', 'Provider', 'Fees', 'Status'].map(h => (
                                        <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.length > 0 ? transactions.map((tx: any, i: number) => (
                                    <TableRow key={tx._id} sx={trHover}>
                                        <TableCell sx={tdStyle}>{dataPage * rowsPerPage + i + 1}</TableCell>
                                        <TableCell
                                            sx={{ ...tdStyle, cursor: 'pointer' }}
                                            onClick={() => {
                                                const u = tx.userId
                                                if (!u?._id) return
                                                navigate('/admin/user-activity', { state: { userId: u._id, userName: `${u.firstName} ${u.lastName}` } })
                                            }}
                                        >
                                            <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{getUserInfo(tx.userId).name}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{getUserInfo(tx.userId).email}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Tooltip title={tx.transactionId} arrow>
                                                <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {tx.transactionId}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#e0e0e0">{fmtDate(tx.createdAt)}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{fmtTime(tx.createdAt)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={14} fontWeight={700} color={type === 'DEPOSIT' ? '#10b981' : '#ef4444'}>
                                                {type === 'DEPOSIT' ? '+' : '-'}${fmt(tx.amount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}><Typography fontSize={13} color="#9a9a9a">${fmt(tx.netAmount)}</Typography></TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Box sx={{ display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '5px', fontSize: 10, fontWeight: 700, color: '#3b82f6', background: '#3b82f615', border: '1px solid #3b82f630' }}>
                                                {tx.paymentProvider}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            {tx.fees?.total > 0
                                                ? <Typography fontSize={12} color="#9a9a9a">${fmt(tx.fees.total)}</Typography>
                                                : <Typography fontSize={11} color="#3a3a3a">—</Typography>}
                                        </TableCell>
                                        <TableCell sx={tdStyle}><StatusPill status={tx.status} /></TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <ArrowDownwardIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>No {type.toLowerCase()}s found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {type === 'TRADE' && (
                    <TableContainer sx={{ maxHeight: '55vh' }}>
                        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                            <TableHead>
                                <TableRow>
                                    {['#', 'User', 'Order ID', 'Date & Time', 'Pair', 'Side', 'Type', 'Filled Quantity', 'Amount', 'Filled', 'Fees', 'Status'].map(h => (
                                        <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.length > 0 ? transactions.map((o: any, i: number) => (
                                    <TableRow key={o._id} sx={trHover}>
                                        <TableCell sx={tdStyle}>{dataPage * rowsPerPage + i + 1}</TableCell>
                                        <TableCell
                                            sx={{ ...tdStyle, cursor: 'pointer' }}
                                            onClick={() => {
                                                const u = o.userId
                                                if (!u?._id) return
                                                navigate('/admin/user-activity', { state: { userId: u._id, userName: `${u.firstName} ${u.lastName}` } })
                                            }}
                                        >
                                            <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{getUserInfo(o.userId).name}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{getUserInfo(o.userId).email}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Tooltip title={o.clientOrderId} arrow>
                                                <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {o.clientOrderId}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#e0e0e0">{fmtDate(o.createdAt)}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{fmtTime(o.createdAt)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {o.crypto?.logo ? (
                                                    <Box
                                                        component="img"
                                                        src={o.crypto.logo}
                                                        alt={o.crypto.symbol || o.currencyPair}
                                                        sx={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }}
                                                        onError={(e: any) => { e.target.style.display = 'none' }}
                                                    />
                                                ) : (
                                                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: '#333', flexShrink: 0 }} />
                                                )}
                                                <Box>
                                                    <Typography fontSize={12} fontWeight={700} color="#e0e0e0" sx={{ lineHeight: 1.2 }}>
                                                        {o.crypto?.name || o.currencyPair}
                                                    </Typography>
                                                    {o.crypto?.symbol && (
                                                        <Typography fontSize={10} color="#a78bfa" sx={{ fontFamily: 'monospace', lineHeight: 1.2 }}>
                                                            {o.crypto.symbol}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={tdStyle}><StatusPill status={o.side} /></TableCell>
                                        <TableCell sx={tdStyle}><StatusPill status={o.type} /></TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#e0e0e0">{o.filledQuantity != null ? o.filledQuantity.toFixed(8) : '—'}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} fontWeight={700} color={o.side === 'BUY' ? '#10b981' : '#ef4444'}>${fmt(o.amount)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#9a9a9a">{o.filledAmount != null ? `$${fmt(o.filledAmount)}` : '—'}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            {(o.platformFee || o.fees)
                                                ? <Tooltip title={`Platform: $${fmt(o.platformFee)} | Provider: $${fmt(o.fees)}`} arrow>
                                                    <Typography fontSize={12} color="#9a9a9a">${fmt((o.platformFee || 0) + (o.fees || 0))}</Typography>
                                                </Tooltip>
                                                : <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                            }
                                        </TableCell>
                                        <TableCell sx={tdStyle}><StatusPill status={o.status} /></TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <SwapHorizIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>No trades found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {type === 'REPAYMENT' && (
                    <TableContainer sx={{ maxHeight: '55vh' }}>
                        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                            <TableHead>
                                <TableRow>
                                    {['#', 'User', 'Transaction ID', 'Type', 'Date & Time', 'Amount', 'Fees', 'Status'].map(h => (
                                        <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.length > 0 ? transactions.map((tx: any, i: number) => (
                                    <TableRow key={tx._id} sx={trHover}>
                                        <TableCell sx={tdStyle}>{dataPage * rowsPerPage + i + 1}</TableCell>
                                        <TableCell
                                            sx={{ ...tdStyle, cursor: 'pointer' }}
                                            onClick={() => {
                                                const u = tx.userId
                                                if (!u?._id) return
                                                navigate('/admin/user-activity', { state: { userId: u._id, userName: `${u.firstName} ${u.lastName}` } })
                                            }}
                                        >
                                            <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{getUserInfo(tx.userId).name}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{getUserInfo(tx.userId).email}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Tooltip title={tx.transactionId} arrow>
                                                <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {tx.transactionId}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <StatusPill status={tx.isAdjustment ? 'SHARE_SELL_DOWN' : tx.isWallet ? 'WALLET' : 'LINK_BANK'} />
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#e0e0e0">{fmtDate(tx.createdAt)}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{fmtTime(tx.createdAt)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={14} fontWeight={700} color="#a78bfa">${fmt(tx.amount)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            {tx.fees?.total > 0
                                                ? <Typography fontSize={12} color="#9a9a9a">${fmt(tx.fees.total)}</Typography>
                                                : <Typography fontSize={11} color="#3a3a3a">—</Typography>}
                                        </TableCell>
                                        <TableCell sx={tdStyle}><StatusPill status={tx.status} /></TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <AccountBalanceIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>No repayments found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    component="div" count={total}
                    page={page} onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); setDataPage(0); setTransactions([]) }}
                    rowsPerPageOptions={[5, 10, 25, 50]} showFirstButton showLastButton
                    sx={{
                        color: '#6a6a6a', borderTop: '1px solid #252525',
                        '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
                        '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
                        '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
                        '& .MuiSelect-select': { color: '#6a6a6a' },
                        '& .MuiSelect-icon': { color: '#6a6a6a' },
                        '& .MuiIconButton-root': { color: '#6a6a6a' },
                        '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
                    }}
                />
            </Box>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
}

export default Transactions